import React from "react";
import { cn } from "@/lib/utils";
import { STATUS_LABELS } from "@/lib/constants";
import { CircleDot, FileText, Hourglass, CheckCircle2, CalendarClock, Send, Sparkles, AlertTriangle, Archive } from "lucide-react";

// Theme-aware status colors. `quiet` = text color for the dot + label (dot uses
// currentColor). `solid` = filled chip (bg/border/text). Each carries a dark:
// variant so status stays legible on both the light and dark themes.
const MAP = {
  inbox:        { icon: CircleDot,     quiet: "text-emerald-700 dark:text-emerald-400", solid: "bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-500/10 dark:border-emerald-500/25 dark:text-emerald-300" },
  draft:        { icon: FileText,      quiet: "text-slate-500 dark:text-slate-400",     solid: "bg-slate-100 border-slate-200 text-slate-600 dark:bg-slate-500/10 dark:border-slate-500/25 dark:text-slate-300" },
  needs_review: { icon: Hourglass,     quiet: "text-amber-700 dark:text-amber-400",     solid: "bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-500/10 dark:border-amber-500/25 dark:text-amber-300" },
  approved:     { icon: CheckCircle2,  quiet: "text-green-700 dark:text-green-400",      solid: "bg-green-50 border-green-200 text-green-700 dark:bg-green-500/10 dark:border-green-500/25 dark:text-green-300" },
  scheduled:    { icon: CalendarClock, quiet: "text-teal-700 dark:text-teal-400",        solid: "bg-teal-50 border-teal-200 text-teal-700 dark:bg-teal-500/10 dark:border-teal-500/25 dark:text-teal-300" },
  publishing:   { icon: Sparkles,      quiet: "text-lime-700 dark:text-lime-400",        solid: "bg-lime-50 border-lime-200 text-lime-700 dark:bg-lime-500/10 dark:border-lime-500/25 dark:text-lime-300" },
  published:    { icon: Send,          quiet: "text-green-700 dark:text-green-400",      solid: "bg-green-50 border-green-200 text-green-700 dark:bg-green-500/10 dark:border-green-500/25 dark:text-green-300" },
  failed:       { icon: AlertTriangle, quiet: "text-red-600 dark:text-red-400",          solid: "bg-red-50 border-red-200 text-red-700 dark:bg-red-500/10 dark:border-red-500/25 dark:text-red-300" },
  archived:     { icon: Archive,       quiet: "text-slate-500 dark:text-slate-400",     solid: "bg-slate-100 border-slate-200 text-slate-600 dark:bg-slate-500/10 dark:border-slate-500/25 dark:text-slate-300" },
};

/**
 * Status indicator.
 *
 *  variant="quiet"  (default) — a small colored dot + colored label, no box.
 *     Status stays fully readable and color-coded, but stops shouting when it
 *     repeats down a list of hundreds of rows. Use this in lists/cards.
 *  variant="solid" — the filled, bordered chip (with icon). Reserve for the
 *     single item being acted on (a detail header), per design-guidelines §2.5.
 */
export function StatusBadge({ status, className, size = "sm", variant = "quiet", showIcon = true, ...rest }) {
  const cfg = MAP[status] || MAP.draft;
  const Icon = cfg.icon;
  const label = STATUS_LABELS[status] || status;

  if (variant === "solid") {
    const padding = size === "lg" ? "px-2.5 py-1" : "px-2 py-0.5";
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full border text-xs font-medium",
          padding,
          cfg.solid,
          className,
        )}
        data-testid={`status-badge-${status}`}
        {...rest}
      >
        {showIcon && Icon ? <Icon className="h-3 w-3" /> : null}
        <span>{label}</span>
      </span>
    );
  }

  // quiet (default): dot + colored label, no fill/border/box.
  const dot = size === "lg" ? "h-2 w-2" : "h-[7px] w-[7px]";
  return (
    <span
      className={cn("inline-flex items-center gap-1.5 text-xs font-semibold", cfg.quiet, className)}
      data-testid={`status-badge-${status}`}
      {...rest}
    >
      <span className={cn("shrink-0 rounded-full bg-current", dot)} aria-hidden />
      <span>{label}</span>
    </span>
  );
}
